import React from "react";
import { NavLink } from "react-router-dom";
import {
    LayoutGrid,
    Radar,
    Palette,
    Youtube,
    CalendarClock,
    Factory,
    CheckSquare,
    Send,
    BarChart3,
    Activity,
    DollarSign,
    ShieldAlert,
    Plug,
    Cpu,
    Server,
    LogOut,
    Crown,
} from "lucide-react";
import { StatusDot } from "@/components/kit";
import { useAuth } from "@/context/AuthContext";

const NAV = [
    { to: "/", icon: LayoutGrid, label: "Command", end: true },
    { to: "/niches", icon: Radar, label: "Nichos" },
    { to: "/kit", icon: Palette, label: "Kit de Canal" },
    { to: "/channels", icon: Youtube, label: "Canales" },
    { to: "/strategy", icon: CalendarClock, label: "Estrategia" },
    { to: "/production", icon: Factory, label: "Producción" },
    { to: "/approvals", icon: CheckSquare, label: "Aprobación" },
    { to: "/publishing", icon: Send, label: "Publicación" },
    { to: "/analytics", icon: BarChart3, label: "Rendimiento" },
    { to: "/retention", icon: Activity, label: "Retención" },
    { to: "/monetization", icon: DollarSign, label: "Monetización" },
    { to: "/integrations", icon: Plug, label: "Integraciones" },
    { to: "/system", icon: Server, label: "Sistema" },
    { to: "/security", icon: ShieldAlert, label: "Seguridad" },
];

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const nav = user?.role === "admin"
        ? [...NAV, { to: "/admin", icon: Crown, label: "Admin" }]
        : NAV;

    return (
        <div className="App grain flex min-h-screen bg-background text-foreground">
            {/* Sidebar */}
            <aside className="fixed left-0 top-0 z-20 flex h-screen w-16 flex-col items-center border-r border-border bg-black py-4 md:w-56 md:items-stretch">
                <div className="mb-6 flex items-center gap-3 px-0 md:px-4">
                    <div className="flex h-8 w-8 items-center justify-center border border-primary text-primary">
                        <Cpu size={16} />
                    </div>
                    <div className="hidden md:block">
                        <div className="font-head text-sm leading-none">AUTOTUBE</div>
                        <div className="text-[10px] tracking-widest text-muted-foreground">
                            ORCHESTRATOR
                        </div>
                    </div>
                </div>

                <nav className="flex flex-1 flex-col gap-1 overflow-y-auto md:px-2">
                    {nav.map((n) => {
                        const Icon = n.icon;
                        return (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.end}
                                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                                className={({ isActive }) =>
                                    `group flex items-center gap-3 border-l-2 px-2 py-2.5 text-xs uppercase tracking-wider transition-colors duration-150 md:px-3 ${
                                        isActive
                                            ? "border-primary bg-accent text-primary"
                                            : "border-transparent text-muted-foreground hover:bg-secondary hover:text-foreground"
                                    }`
                                }
                            >
                                <Icon size={16} className="shrink-0" />
                                <span className="hidden md:inline">{n.label}</span>
                            </NavLink>
                        );
                    })}
                </nav>

                {/* Account */}
                <div className="mt-4 border-t border-border pt-3 md:px-2">
                    <div className="flex items-center justify-center gap-2 md:justify-start md:px-1">
                        {user?.picture ? (
                            <img src={user.picture} alt="" className="h-7 w-7 rounded-full border border-border" />
                        ) : (
                            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-[10px]">
                                {(user?.name || user?.email || "?")[0]?.toUpperCase()}
                            </div>
                        )}
                        <div className="hidden min-w-0 flex-1 md:block">
                            <div className="truncate text-[11px] font-medium">{user?.name}</div>
                            <div className="truncate text-[9px] uppercase tracking-wider text-muted-foreground">
                                {user?.role}
                            </div>
                        </div>
                    </div>
                    <button
                        data-testid="logout-btn"
                        onClick={logout}
                        className="mt-2 flex w-full items-center justify-center gap-2 border border-border py-2 text-[10px] uppercase tracking-wider text-muted-foreground transition-colors hover:border-destructive hover:text-destructive md:justify-start md:px-2"
                    >
                        <LogOut size={13} className="shrink-0" />
                        <span className="hidden md:inline">Cerrar sesión</span>
                    </button>
                </div>
            </aside>

            {/* Main */}
            <main className="relative z-10 ml-16 min-w-0 flex-1 md:ml-56">
                <header className="sticky top-0 z-10 flex h-12 items-center justify-between border-b border-border bg-black px-4 md:px-6">
                    <div className="flex items-center gap-2 truncate text-[10px] tracking-widest text-muted-foreground">
                        <span className="text-primary">◢</span>
                        <span className="truncate">{user?.email}</span>
                    </div>
                    <div className="hidden items-center gap-4 text-[10px] tracking-widest text-muted-foreground sm:flex">
                        <span className="flex items-center gap-1.5">
                            <StatusDot color="#39FF14" /> EDGE-TTS
                        </span>
                        <span className="flex items-center gap-1.5">
                            <StatusDot color="#39FF14" /> FFMPEG
                        </span>
                    </div>
                </header>
                <div className="min-w-0 p-4 md:p-6">{children}</div>
            </main>
        </div>
    );
}
